import { redirect, useNavigate, useRouteLoaderData } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { AuthLoaderFunction } from '@/types/auth';
import { filter, find } from 'lodash';
import { useOrganizationStore } from '@/stores/organizationStore';
import { WorkspaceFromStore } from '@/types/workspaces';
import { NAV_ROUTES } from '@/utils/constants';
import { useWorkspaceIdStore } from '@/stores/workspaceIdStore';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';
import { OrganizationWithWorkspaces } from '@/types/Organization';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { UserService } from '@/services/userService';
import { hasPermission } from '@/auth/hasPermission';
import { getGroupedWorkspaces, getHomeRedirectUrlPerRole } from './helpers/utils';

export const WorkSpacesList = () => {
  const navigate = useNavigate();
  const { workspaces } = useRouteLoaderData('workspaces') as {
    workspaces: WorkspaceFromStore[];
  };
  const selectedOrganization = useOrganizationStore.getState().selectedOrganization;
  const workspaceId = useWorkspaceIdStore((state) => state.workspaceId);
  const urlWorkspaceId = useWorkspaceIdFromParams();
  const workspacesPossibleToSelect = useMemo(
    () => filter(workspaces, ({ status }) => status !== 'DELETION_AWAITING'),
    [workspaces],
  );
  const workspaceUserRole = useUserPermissionsStore(
    (state) => state.workspacePermission?.role,
  );
  const mainDir = useMemo(
    () => getHomeRedirectUrlPerRole(workspaceUserRole),
    [workspaceUserRole],
  );

  const handleRedirectWhenOrganizationIsSelected = useCallback(() => {
    if (workspaceId && hasPermission('workspace', 'connectors', 'view')) {
      navigate(`${mainDir}?workspaceId=${workspaceId}`, {
        replace: true,
      });
      // possibly impossible state, but just in case
    } else if (workspacesPossibleToSelect.length > 0) {
      navigate(`${mainDir}?workspaceId=${workspacesPossibleToSelect[0].id}`, {
        replace: true,
      });
    }
  }, [mainDir, navigate, workspaceId, workspacesPossibleToSelect]);

  const handleRedirectWhenOrganizationIsNotSelected = useCallback(() => {
    if (workspacesPossibleToSelect.length > 0) {
      const organizationId = workspacesPossibleToSelect[0].organizationId;
      const organization = find(
        useOrganizationStore.getState().organizations,
        ({ id }) => id === organizationId,
      );
      useOrganizationStore
        .getState()
        .setSelectedOrganization(organization as OrganizationWithWorkspaces);
      navigate(`${mainDir}?workspaceId=${workspacesPossibleToSelect[0].id}`, {
        replace: true,
      });
    }
  }, [mainDir, navigate, workspacesPossibleToSelect]);

  useEffect(() => {
    if (urlWorkspaceId) {
      navigate(`${mainDir}?workspaceId=${urlWorkspaceId}`, {
        replace: true,
      });
    } else if (
      selectedOrganization &&
      selectedOrganization?.status !== 'AWAITING_DELETION'
    ) {
      handleRedirectWhenOrganizationIsSelected();
    } else if (!selectedOrganization && workspaces.length > 0) {
      handleRedirectWhenOrganizationIsNotSelected();
    }
  }, [
    handleRedirectWhenOrganizationIsNotSelected,
    handleRedirectWhenOrganizationIsSelected,
    mainDir,
    navigate,
    selectedOrganization,
    selectedOrganization?.status,
    urlWorkspaceId,
    workspaceId,
    workspaces.length,
    workspacesPossibleToSelect,
  ]);

  return null;
};

export const workspacesLoader: AuthLoaderFunction = async (args, { userId }) => {
  const { organizations, workspaces } = await getGroupedWorkspaces(userId);
  const selectedOrganization = useOrganizationStore.getState().selectedOrganization;
  useOrganizationStore.getState().setOrganizations(organizations);
  const selectedFromOrganizations = find(
    organizations,
    ({ id }) => id === selectedOrganization?.id,
  );
  if (selectedOrganization?.status === 'AWAITING_DELETION') {
    useOrganizationStore.getState().setSelectedOrganization(null);
  } else {
    useOrganizationStore
      .getState()
      .setSelectedOrganization(selectedFromOrganizations || null);
  }

  const url = new URL(args.request.url);
  if (
    url.pathname === '/' &&
    workspaces.length === 0 &&
    !url.searchParams.has('priceId')
  ) {
    const [workspaceInvitations, organizationInvitations] = await Promise.all([
      UserService.getWorkspacesInvitations(userId),
      UserService.getOrganizationsInvitations(userId),
    ]);
    if (workspaceInvitations.length + organizationInvitations.length === 0) {
      return redirect(NAV_ROUTES.subscriptions);
    }
  }

  return { workspaces, userId };
};
